import { LocalTime } from '@js-joda/core';
import isUndefined from 'lodash/isUndefined';

import {
   AvailabilityPractitioner,
   AvailabilityTypeSimple,
   HealthcareProviderLocationIdentifier,
} from '@dreryk/egabinet-appointment-frontend-api';
import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { commonTranslationKeys } from '@dreryk/react-common';
import {
   Checkbox,
   DatePicker,
   dayValidator,
   GridLayout,
   LargeAreaTemplate,
   RadioGroup,
   StyledFlex,
   TextView,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { personnelTranslationKeys } from '../../translations';
import { AvailabilityTypeSelect } from '../availabilityCreation/AvailabilityTypeSelect';
import { useDisabledDaySelector } from '../common/common.hooks';
import { FormType, RecurrencePeriod, RecurrencePeriodUnion, TimePickerRangeType } from '../common/common.types';
import { recurrenceTypes, validateAvailabilityDate } from '../common/common.utils';
import { DocplannerServicesInformationNotification } from '../common/components/DocplannerServicesInformationNotification';
import { AvailabilityHealthcareProviderLocationSelect } from '../common/fields/AvailabilityHealthcareProviderLocation/AvailabilityHealthcareProviderLocationSelect';
import { AvailabilityTimeRange } from '../common/fields/AvailabilityTimeRange';
import { AvailabilityTimeSlotSection } from '../common/fields/AvailabilityTimeSlot';
import { SendToDocplanner } from '../common/fields/SendToDocplanner';
import { SendToPatientPortal } from '../common/fields/SendToPatientPortal';
import { EncounterModeCheckboxes } from '../common/fields/EncounterMode/EncounterModeCheckboxes';

import { AvailabilityUpdateFormController } from './availabilityUpdate.types';

type AvailabilityUpdatePanelProps = {
   readonly register: AvailabilityUpdateFormController['register'];
   readonly watch: AvailabilityUpdateFormController['watch'];
   readonly formState: AvailabilityUpdateFormController['formState'];
   readonly trigger: AvailabilityUpdateFormController['trigger'];
   readonly availabilityTypes: AvailabilityTypeSimple[];
   readonly recurrencePeriodSupplier: () => RecurrencePeriod;
   readonly recurringAvailability: boolean;
   readonly adjustStartTimeOrEndTime: (rangeType: TimePickerRangeType, value: LocalTime, stepInMinutes: number) => void;
   readonly practitioner?: AvailabilityPractitioner;
   readonly defaultLocationIdentifier?: HealthcareProviderLocationIdentifier;
   readonly healthcareLocations: HealthcareProviderLocation[];
};

const AVAILABILITY_CREATION_PANEL_GRID_AREA_WITH_SPECIALIST: LargeAreaTemplate = [
   'e1 e1 e1 . . . . . . . . . .',
   'e2 e2 e2 . . . . . . . . . .',
   'e3 e3 e3 e3 . . . . . . . . .',
   'e4 e4 e4 . . . . . . . . . .',
   'e5 e5 e5 . . . . . . . . . .',
   'e6 e6 e6 e6 e6 . . . . . . . .',
   'e7 e7 e7 e7 . . . . . . . . .',
   'e8 e8 e8 e8 . . . . . . . . .',
];
const AVAILABILITY_CREATION_PANEL_GRID_AREA: LargeAreaTemplate = [
   'e1 e1 e1 . . . . . . . . . .',
   'e2 e2 e2 e2 . . . . . . . . .',
   'e3 e3 e3  . . . . . . . . . .',
   'e4 e4 e4 . . . . . . . . . .',
   'e5 e5 e5 e5 e5 . . . . . . . .',
   'e6 e6 e6 e6 . . . . . . . . .',
];

// eslint-disable-next-line max-lines-per-function
export const AvailabilityUpdatePanel = (props: AvailabilityUpdatePanelProps): JSX.Element => {
   const { register, watch, formState, trigger, availabilityTypes } = props;
   const { disabledDaySelector } = useDisabledDaySelector(props.recurrencePeriodSupplier, FormType.UPDATE);

   const { sendToDocplanner, sendToPatientPortal } = props.watch(['sendToPatientPortal', 'sendToDocplanner']);

   return (
      <GridLayout
         area={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            props.practitioner
               ? AVAILABILITY_CREATION_PANEL_GRID_AREA_WITH_SPECIALIST
               : AVAILABILITY_CREATION_PANEL_GRID_AREA
         }
         testId={'availabilityCreation'}
      >
         {!isUndefined(props.practitioner) && (
            <TextView label={personnelTranslationKeys.specialist} testId={'specialist'}>
               {props.practitioner.firstName.asString()} {props.practitioner.lastName.asString()}
            </TextView>
         )}
         <StyledFlex direction={'column'} gap={'24px'}>
            <DatePicker
               testId={'availabilityDate'}
               label={commonTranslationKeys.date}
               disabled
               controller={register('availabilityDate', {
                  required: true,
                  validate: (data, getValue) => {
                     return validateAvailabilityDate(data, getValue, FormType.UPDATE);
                  },
               })}
               dayValidators={[dayValidator.custom(disabledDaySelector)]}
               withNavigationArrows
            />
            {props.recurringAvailability ? (
               <Checkbox
                  testId={'updateSingleOccurrence'}
                  controller={register('updateSingleOccurrence')}
                  label={personnelTranslationKeys.concernsOnlySelectedAvailability}
               />
            ) : null}
         </StyledFlex>
         <AvailabilityTimeRange register={register} adjustStartTimeOrEndTime={props.adjustStartTimeOrEndTime} />
         <RadioGroup<RecurrencePeriodUnion>
            disabled
            testId={'recurrencePeriod'}
            label={personnelTranslationKeys.recurrencePeriod}
            items={recurrenceTypes}
            controller={props.register('recurrencePeriod', {
               required: true,
            })}
         />
         <AvailabilityTypeSelect register={props.register} availabilityTypes={availabilityTypes} />
         <AvailabilityTimeSlotSection register={register} watch={watch} formState={formState} trigger={trigger} />
         <AvailabilityHealthcareProviderLocationSelect
            register={props.register}
            defaultLocationIdentifier={props.defaultLocationIdentifier}
            healthcareLocations={props.healthcareLocations}
         />
         <StyledFlex direction={'column'} gap={'12px'}>
            <SendToDocplanner register={props.register} trigger={trigger} />
            {protectFeature(
               'bookingVideoConsultations',
               sendToDocplanner ? (
                  <EncounterModeCheckboxes register={props.register} name={'docplannerEncounterMode'} watch={watch} />
               ) : null
            )}

            <SendToPatientPortal register={props.register} trigger={trigger} />
            {protectFeature(
               'bookingVideoConsultations',
               sendToPatientPortal ? (
                  <EncounterModeCheckboxes
                     register={props.register}
                     name={'patientPortalEncounterMode'}
                     watch={watch}
                  />
               ) : null
            )}

            <DocplannerServicesInformationNotification />
         </StyledFlex>
      </GridLayout>
   );
};
